import { useCallback } from 'react';

import { useLazyGetSessionQuery } from 'services/auth/auth';

import { setUser } from 'store/user/userSlice';

import { clearStaffViewAsPatient, SESSION_KEYS } from 'utils/storage';
import { clearWorkOSCookies } from 'utils/workos';

import { useAppDispatch } from 'hooks';

type LoadSessionOptions = { sweepStaffContext?: boolean };

/**
 * Provides a function that loads the WorkOS session from the backend,
 * dispatches user state, and stores the socket access token.
 *
 * Returns `true` if the session was loaded successfully, `false` otherwise.
 *
 * Loading a cookie session is a direct sign-in, so by default it drops the tab's staff
 * view-as-patient context first. A caller that is starting a staff sign-in in the same tab passes
 * `sweepStaffContext: false`: that sign-in marks the tab itself, and the sweep would race it.
 */
export const useWorkOSSession = () => {
  const dispatch = useAppDispatch();
  const [getSession] = useLazyGetSessionQuery();

  const loadSession = useCallback(
    async (options: LoadSessionOptions = {}): Promise<boolean> => {
      const { sweepStaffContext = true } = options;
      try {
        const sessionData = await getSession().unwrap();
        if (sessionData?.data?.authenticated) {
          // Defense in depth: the store drops the staff context whenever a session is cleared, so
          // this only catches one that was left in the tab by a session that ended before that
          // existed (sessionStorage outlives a deploy) or that was written after its session ended.
          if (sweepStaffContext) clearStaffViewAsPatient();
          dispatch(setUser(sessionData.data));
          if (sessionData.data.socketAccessToken) {
            sessionStorage.setItem(
              SESSION_KEYS.SOCKET_ACCESS_TOKEN,
              sessionData.data.socketAccessToken
            );
          }
          return true;
        }
      } catch {
        // Session invalid or expired
      }
      // Clear stale WorkOS markers so prepareHeaders doesn't skip the
      // Authorization header on subsequent JWT-based requests.
      clearWorkOSCookies();
      return false;
    },
    [dispatch, getSession]
  );

  return { loadSession };
};
